import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { apiClient, ssoCookieClient } from '@/shared/lib/axios';
import {
  completeActiveSessionSync,
  emitActiveSessionAuthGeneration,
  normalizeActiveSessionSyncTransportError,
  resolveActiveSessionSyncConfig,
  settleActiveSessionSyncCooldown,
  takeActiveSessionSyncReturnPath,
  takeSsoIntendedPath,
  type ActiveSessionSyncCompletionOutcome,
} from './activeSessionSync';
import { runP1SsoAuthMutation } from './ssoAuthMutationLock';
import {
  clearPortalSession,
  clearSsoDenial,
  completeSsoSession,
  isSsoAccessDeniedError,
  isSsoEnabled,
  isSsoSite,
  readRememberedSsoDenial,
  rememberSsoDenial,
  ssoLoginPath,
  ssoReturnPath,
  validateDeniedBrokerLogoutUrl,
  type SsoDenialVerdict,
  type SsoEntryType,
} from './ssoSession';

interface SsoCompletionPageProps {
  entryType: SsoEntryType;
}

interface SsoCompletionStatusProps {
  failed: boolean;
  denied?: boolean;
  deniedActionUrl?: string;
  loginPath: string;
}

export const SsoCompletionStatus = ({
  failed,
  denied = false,
  deniedActionUrl,
  loginPath,
}: SsoCompletionStatusProps) => {
  if (denied) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <h1 className="mb-3 text-xl font-bold text-red-600">
            アクセス権限がありません
          </h1>
          <p className="mb-6 text-sm text-gray-600">
            このシステムを利用できるアカウントが登録されていません。
          </p>
          {deniedActionUrl ? (
            <a
              href={deniedActionUrl}
              className="inline-block rounded-md bg-blue-600 px-6 py-2 text-white hover:bg-blue-700"
            >
              SSOからログアウト
            </a>
          ) : (
            <p role="alert" className="text-sm font-medium text-red-700">
              安全なログアウト先を確認できません。時間をおいて再試行してください。
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!failed) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
        <h1 className="mb-3 text-xl font-bold text-red-600">
          SSOログインを完了できませんでした。
        </h1>
        <p className="mb-6 text-sm text-gray-600">
          もう一度ログインするか、管理者にお問い合わせください。
        </p>
        <a
          href={loginPath}
          className="inline-block rounded-md bg-blue-600 px-6 py-2 text-white hover:bg-blue-700"
        >
          ログイン画面へ
        </a>
      </div>
    </div>
  );
};

const SsoCompletionPage = ({ entryType }: SsoCompletionPageProps) => {
  const { site } = useParams<{ site: string }>();
  const started = useRef(false);
  const [status, setStatus] = useState<'pending' | 'failed' | 'denied'>('pending');
  const [deniedActionUrl, setDeniedActionUrl] = useState<string>();
  const validSite = isSsoSite(site) ? site : undefined;
  const context = useMemo(
    () => validSite ? { site: validSite, entryType } : undefined,
    [entryType, validSite],
  );

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    if (window.location.search || window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (!context) {
      setStatus('failed');
      return;
    }

    const loadSessionMetadata = async (endpoint: string): Promise<unknown> => {
      const response = await ssoCookieClient.get(endpoint, { withCredentials: true });
      return response.data?.data;
    };
    const activeSyncConfig = resolveActiveSessionSyncConfig(
      import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED,
      import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_COOLDOWN_MS,
    );
    // Broker standard/passive callbacks are always two-phase when SSO is on.
    // The active-sync flag controls only foreground mount/focus checks.
    const ssoActive =
      isSsoEnabled(import.meta.env.VITE_SSO_ENABLED) ||
      isSsoEnabled(import.meta.env.VITE_SSO_PASSIVE_ENABLED);
    const ssoMutationEnabled =
      ssoActive ||
      isSsoEnabled(import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED);
    if (activeSyncConfig || ssoActive) {
      const activeSyncReturn = takeActiveSessionSyncReturnPath(
        context,
        window.sessionStorage,
      );
      void runP1SsoAuthMutation({
        context,
        ssoEnabled: ssoMutationEnabled,
        storage: window.localStorage,
        operation: async () => {
          const outcome = await completeActiveSessionSync({
            context,
            rememberedReturnPath: activeSyncReturn.returnPath,
            activeSyncAttempted: activeSyncReturn.activeSyncAttempted,
            requestCompletion: async (endpoint, payload) => {
              try {
                const response = await apiClient.post(endpoint, payload, { withCredentials: true });
                return {
                  statusCode: response.status,
                  payload: response.data?.data,
                };
              } catch (error) {
                return normalizeActiveSessionSyncTransportError(error);
              }
            },
            loadSessionMetadata,
            localStorage: window.localStorage,
            sessionStorage: window.sessionStorage,
            eventTarget: window,
          });

          if (outcome.kind !== 'legacy') {
            return outcome;
          }

          const deniedOutcome = (
            verdict: SsoDenialVerdict,
          ): ActiveSessionSyncCompletionOutcome => ({
            kind: 'denied',
            returnPath: ssoReturnPath(context),
            reason: verdict.reason ?? 'PORTAL_ACCESS_DENIED',
            ...(verdict.logoutUrl === undefined
              ? {}
              : { logoutUrl: verdict.logoutUrl }),
          });

          try {
            const returnPath = await completeSsoSession(
              context,
              loadSessionMetadata,
              window.localStorage,
              window.sessionStorage,
            );
            emitActiveSessionAuthGeneration(context, window.localStorage, window);
            settleActiveSessionSyncCooldown(
              context,
              window.sessionStorage,
              window.localStorage,
            );
            return { kind: 'hydrate', returnPath } as const;
          } catch (error) {
            clearPortalSession(
              context,
              window.localStorage,
              window.sessionStorage,
            );
            emitActiveSessionAuthGeneration(context, window.localStorage, window);

            // The probe is only reached when the broker left nothing to complete for this
            // browser, so its error is the only remaining signal. An explicit 403 is an
            // access decision; otherwise fall back to the denial this context already
            // received, because the completion page outliving its one-shot cookie must not
            // downgrade "no account here" into "SSO could not be completed" — that message
            // reads as an outage and sends the user round the login loop again.
            if (isSsoAccessDeniedError(error)) {
              return deniedOutcome({});
            }

            const remembered = readRememberedSsoDenial(
              context,
              window.sessionStorage,
            );
            if (remembered !== undefined) {
              return deniedOutcome(remembered);
            }

            throw error;
          }
        },
      })
        .then(result => {
          if (result.kind === 'skipped') {
            if (result.reason === 'stale_intent') {
              window.location.replace(activeSyncReturn.returnPath);
              return;
            }
            setStatus('failed');
            return;
          }

          const outcome = result.value;
          if (outcome.kind === 'denied') {
            // "There is no account for you HERE" is not a decision about this person, it is the
            // ordinary state of a portal they were never given. Sending them to this portal's own
            // login form lets them sign in with the credentials they do have; the dead-end screen
            // is kept for the answers that ARE a decision (inactive account, scope mismatch,
            // conflicting mapping), where a second attempt cannot help and the honest move is to
            // say so and offer the way out.
            if (outcome.reason === 'SSO_ACCOUNT_NOT_LINKED') {
              clearSsoDenial(context, window.sessionStorage);
              window.location.replace(`${ssoLoginPath(context)}?sso_passive=checked`);
              return;
            }
            rememberSsoDenial(
              context,
              { reason: outcome.reason, logoutUrl: outcome.logoutUrl },
              window.sessionStorage,
            );
            setDeniedActionUrl(validateDeniedBrokerLogoutUrl(
              outcome.logoutUrl,
              context,
              window.location.origin,
            ));
            setStatus('denied');
            return;
          }
          if (outcome.kind === 'failed') {
            setStatus('failed');
            return;
          }
          if (outcome.kind === 'hydrate' || outcome.kind === 'signed_out') {
            // A newer authoritative answer supersedes any remembered denial, so a mapping
            // added since then converges instead of sticking on the no-access screen.
            clearSsoDenial(context, window.sessionStorage);
          }
          // The page the visitor actually asked for, if the login screen recorded one before
          // handing the browser to the broker. Only for a session that now EXISTS: after a
          // sign-out the intent is stale, and honouring it would bounce them at the guard.
          const intended = outcome.kind === 'hydrate'
            ? takeSsoIntendedPath(context, window.sessionStorage)
            : undefined;
          window.location.replace(intended ?? outcome.returnPath);
        })
        .catch(() => {
          setStatus('failed');
        });
      return;
    }

    setStatus('failed');
  }, [context]);

  // A failed completion must not bounce the user straight back into the broker round trip
  // that just failed. The login page treats `sso_passive=checked` as "passive SSO already
  // resolved for this page load", so the user lands on the form and stays there. Without it
  // the passive marker expires after 15s while the error is still on screen, and reading the
  // message for longer than that was enough to make the login page redirect again — an
  // inescapable loop between the login page and this error.
  const loginPath = context
    ? `${ssoLoginPath(context)}?sso_passive=checked`
    : '/';

  return (
    <SsoCompletionStatus
      failed={status === 'failed'}
      denied={status === 'denied'}
      deniedActionUrl={deniedActionUrl}
      loginPath={loginPath}
    />
  );
};

export default SsoCompletionPage;
